import { globalizeSelectors } from '../Utils';
import { fulfilled, rejected } from '../factories/ApiCall';
import type {
  LoadTranslationProposalsPayload,
  SaveTranslationsPayload,
  TranslationLanguage,
  TranslationProposalResponse,
  TranslationsAction,
  TranslationsState,
} from '../types/Translations';

const types = {
  SAVE_TRANSLATIONS: 'TRANSLATIONS/SAVE_TRANSLATIONS',
  LOAD_TRANSLATION_PROPOSALS: 'TRANSLATIONS/LOAD_TRANSLATION_PROPOSALS',
} as const;

const actions = {
  saveTranslations: ({
    siteid,
    data,
  }: SaveTranslationsPayload): TranslationsAction<SaveTranslationsPayload> => ({
    type: types.SAVE_TRANSLATIONS,
    payload: { siteid, data },
  }),
  loadProposals: (
    language: TranslationLanguage
  ): TranslationsAction<LoadTranslationProposalsPayload> => ({
    type: types.LOAD_TRANSLATION_PROPOSALS,
    payload: { language },
  }),
};

const mountPath = 'translations';

const initialState: TranslationsState = {
  pending: false,
  error: null,
  proposals: [],
  proposals_pending: false,
  proposals_error: null,
};

function payloadAs<Payload>(action: TranslationsAction): Payload {
  return action.payload as Payload;
}

function reducer(
  state: TranslationsState = initialState,
  action: TranslationsAction
): TranslationsState {
  switch (action.type) {
    case types.SAVE_TRANSLATIONS: {
      return { ...state, pending: true, error: null };
    }
    case fulfilled(types.SAVE_TRANSLATIONS): {
      return { ...state, pending: false };
    }
    case rejected(types.SAVE_TRANSLATIONS): {
      return { ...state, pending: false, error: action.payload };
    }
    case types.LOAD_TRANSLATION_PROPOSALS: {
      return {
        ...state,
        proposals_pending: true,
        proposals_error: null,
      };
    }
    case fulfilled(types.LOAD_TRANSLATION_PROPOSALS): {
      return {
        ...state,
        proposals: payloadAs<TranslationProposalResponse>(action) || [],
        proposals_pending: false,
      };
    }
    case rejected(types.LOAD_TRANSLATION_PROPOSALS): {
      return {
        ...state,
        proposals_pending: false,
        proposals_error: action.payload,
      };
    }
    default:
      return state;
  }
}

const localSelectors = {
  pending: (state: TranslationsState): boolean => state.pending,
  error: (state: TranslationsState): TranslationsState['error'] => state.error,
  proposals: (state: TranslationsState): TranslationProposalResponse =>
    state.proposals,
  proposals_pending: (state: TranslationsState): boolean =>
    state.proposals_pending,
  proposals_error: (
    state: TranslationsState
  ): TranslationsState['proposals_error'] => state.proposals_error,
};

const selectors = globalizeSelectors(localSelectors, mountPath);

export { types as actionTypes, actions as default, selectors, reducer };
